import { useTranslate } from "#src/hooks";
import { Button } from "@libertify/ui";
import { Flex, Modal } from "antd";
import { useCallback, useRef, useState } from "react";
import { ContactResponse, PresenterProfileResponse } from "../../types";
import { AvatarDetails } from "./AvatarDetails";

export type AvatarEditModalProps = {
  open: boolean;
  contact: ContactResponse | undefined;
  onClose: () => void;
  onSaved: (
    contact: ContactResponse,
    updatedPresenter: PresenterProfileResponse | undefined
  ) => Promise<void> | void;
};

export const AvatarEditModal = ({
  open,
  contact,
  onClose,
  onSaved,
}: AvatarEditModalProps) => {
  const t = useTranslate();
  const [saving, setSaving] = useState(false);
  const [submitButtonEnabled, setSubmitButtonEnabled] = useState(true);
  const saveCallback = useRef<() => Promise<void>>();
  const savedPresenter = useRef<PresenterProfileResponse | undefined>();

  const handleSave = useCallback(async () => {
    if (!contact) return;
    try {
      setSaving(true);
      await saveCallback.current?.();
      await onSaved(contact, savedPresenter.current);
    } catch (e) {
      console.error(e);
    } finally {
      setSaving(false);
    }
  }, [contact, onSaved]);

  return (
    <Modal
      open={open}
      width="80vw"
      style={{ maxWidth: 1000 }}
      onCancel={onClose}
      destroyOnHidden
      // Stacks on top of ChapterAvatarModal (zIndex 3100).
      zIndex={3200}
      footer={
        <Flex justify="center" gap={12} wrap="wrap" style={{ width: "100%" }}>
          <Button variant="outlined" onClick={onClose}>
            {t("buttons.cancel")}
          </Button>
          <Button
            variant="primary"
            disabled={!submitButtonEnabled}
            loading={saving}
            onClick={handleSave}
          >
            {t("components.ContactWithPresenter.AvatarEdit.saveAvatar")}
          </Button>
        </Flex>
      }
      title={t(
        "components.templates.settings.presenterTab.editAvatarModalTitle"
      )}
    >
      {contact && (
        <AvatarDetails
          id={contact.id}
          contact={contact}
          presenter={{
            data: (contact.presenter as PresenterProfileResponse) ?? null,
          }}
          setSubmitButtonEnabled={setSubmitButtonEnabled}
          saveCallback={saveCallback}
          onSuccess={(_id, updatedPresenter) => {
            savedPresenter.current = updatedPresenter;
          }}
          style={{ width: "100%" }}
        />
      )}
    </Modal>
  );
};
